import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { useDispatch, useSelector } from 'react-redux';
import { Wrapper } from '../../components/Wrapper';
import { getAllRequestInvoiceTable, requestInvoiceApproved } from '../../redux/actions/request';
import { getTotalRequestInvoicePending } from '../../redux/actions/analytics';
import { onlyInvoice, optionsSort, optionsStatusRequestInvoiceFilter } from '../../static/data';
import { DrawerRequestInvoicePaymentDetails } from './DrawerRequestInvoicePaymentDetails';
import { SmartTable } from '../../components/smartTable/SmartTable';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { useTablePrefs } from '../../hooks/useTablePrefs';
import toast from 'react-hot-toast';
import { buildRequestInvoiceColumns } from './requestInvoice.columns';
import { PopoverCheckboxFilter } from '../../components/popoverCheckboxFilter/PopoverCheckboxFilter';
import { PopoverRangeDate } from '../../components/popoverRangeDate/PopoverRangeDate';
import { PopoverRadioFilter } from '../../components/popoverRadioFilter/PopoverRadioFilter';
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid';

const TABLE_KEY = 'control-facturas';

const DEFAULT_PREFS = {
	query: {
		page: 0,
		pageSize: 15,
		sorting: [{ id: 'createdAt', desc: true }],
		filters: {
			status: [],
			fromDate: '',
			toDate: '',
		},
		columnSearch: {},
	},
	view: {
		columnVisibility: {},
		columnOrder: [],
		columnSizing: {},
	},
};

/** Acepta legacy `status` string en prefs y lo normaliza a `string[]`. */
const normalizeInvoiceStatusFilter = (status) => {
	if (status == null || status === '') return [];
	if (Array.isArray(status)) return status.map((s) => String(s).trim()).filter(Boolean);
	return [String(status).trim()].filter(Boolean);
};

const sortingToApi = (sorting) => {
	if (!Array.isArray(sorting) || sorting.length === 0) {
		return { field: 'createdAt', dir: 'desc' };
	}
	const [first] = sorting;
	return { field: first.id, dir: first.desc ? 'desc' : 'asc' };
};

const buildApiPayload = (query) => ({
	page: query.page,
	pageSize: query.pageSize,
	filters: query.filters || {},
	columnSearch: query.columnSearch || {},
	sort: sortingToApi(query.sorting),
	usePagination: true,
});

export const RequestInvoicePage = () => {
	const dispatch = useDispatch();
	const { user } = useSelector((state) => state.user);
	const { roles, typeUser, _id: userId } = user;
	const { requestsInvoice, loading } = useSelector((state) => state.request);
	const [prefs, , patchPrefs, resetPrefs, prefsHydrated] = useTablePrefs(TABLE_KEY, DEFAULT_PREFS);
	const [totalRows, setTotalRows] = useState(0);
	const [isOpenDrawerPaymentDetails, setIsOpenDrawerPaymentDetails] = useState(false);
	const [requestIdSelected, setRequestIdSelected] = useState('');
	const [formData, setFormData] = useState({
		status: normalizeInvoiceStatusFilter(prefs.query.filters?.status),
		fromDate: prefs.query.filters?.fromDate || null,
		toDate: prefs.query.filters?.toDate || null,
	});

	useEffect(() => {
		onlyInvoice.includes(typeUser) && dispatch(getTotalRequestInvoicePending('pending'));
	}, [dispatch, typeUser]);

	if (roles !== 'admin') {
		window.location.href = '/';
	}

	const patchQuery = useCallback((patch) => {
		patchPrefs({ query: { ...prefs.query, ...patch } });
	}, [patchPrefs, prefs.query]);

	const patchView = useCallback((patch) => {
		patchPrefs({ view: { ...prefs.view, ...patch } });
	}, [patchPrefs, prefs.view]);

	useEffect(() => {
		setFormData({
			status: normalizeInvoiceStatusFilter(prefs.query.filters?.status),
			fromDate: prefs.query.filters?.fromDate || null,
			toDate: prefs.query.filters?.toDate || null,
		});
	}, [prefs.query.filters]);

	const handleColumnSearchChange = useCallback((columnId, value) => {
		const current = prefs.query.columnSearch || {};
		const trimmed = (value || '').trim();
		const next = { ...current };
		if (trimmed) {
			next[columnId] = trimmed;
		} else {
			delete next[columnId];
		}
		patchQuery({ page: 0, columnSearch: next });
	}, [patchQuery, prefs.query.columnSearch]);

	const queryKey = JSON.stringify(prefs.query);
	useEffect(() => {
		if (!prefsHydrated) return;
		dispatch(getAllRequestInvoiceTable(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) {
				setTotalRows(result.totalRows || 0);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey, dispatch, prefsHydrated]);

	const applyFilters = useCallback((nextFormData = formData) => {
		const statusList = normalizeInvoiceStatusFilter(nextFormData.status);
		patchQuery({
			page: 0,
			filters: {
				...prefs.query.filters,
				status: statusList,
				fromDate: nextFormData.fromDate || '',
				toDate: nextFormData.toDate || '',
			},
		});
	}, [formData, patchQuery, prefs.query.filters]);

	const currentSortValue = (prefs.query.sorting?.[0]?.desc ?? true) ? 'desc' : 'asc';
	const handleSortChange = useCallback((value) => {
		patchQuery({
			page: 0,
			sorting: [{ id: 'createdAt', desc: value === 'desc' }],
		});
	}, [patchQuery]);

	const removeFilters = useCallback(() => {
		const cleared = { status: [], fromDate: null, toDate: null };
		setFormData(cleared);
		patchQuery({
			page: 0,
			filters: { ...prefs.query.filters, status: [], fromDate: '', toDate: '' },
			columnSearch: {},
		});
	}, [patchQuery, prefs.query.filters]);

	const handleOpenPaymentDetails = useCallback((requestId) => {
		if (!requestId) return;
		setRequestIdSelected(requestId);
		setIsOpenDrawerPaymentDetails(true);
	}, []);

	const toggleDrawerPaymentDetails = useCallback(() => {
		setIsOpenDrawerPaymentDetails((prev) => {
			if (prev) setRequestIdSelected('');
			return !prev;
		});
	}, []);

	const handleConfirmRequest = useCallback((requestId) => {
		dispatch(requestInvoiceApproved(requestId, userId))
			.then((response) => {
				if (response?.status === 200) {
					toast.success(response?.message);
					dispatch(getTotalRequestInvoicePending('pending'));
					dispatch(getAllRequestInvoiceTable(buildApiPayload(prefs.query))).then((result) => {
						if (result?.status === 200) setTotalRows(result.totalRows || 0);
					});
				}
			})
			.catch(() => {
				toast.error('Error al confirmar la solicitud');
			});
	}, [dispatch, prefs.query, userId]);

	const columns = useMemo(
		() => buildRequestInvoiceColumns({
			onOpenDetails: handleOpenPaymentDetails,
			onConfirm: handleConfirmRequest,
			typeUser,
		}),
		[handleConfirmRequest, handleOpenPaymentDetails, typeUser],
	);

	return (
		<ContainerFull>
			<Heading
				title='Control de facturas'
				subtitle='Historial de solicitudes de facturas'
				center={false}
			/>

			<div className='mt-4 mb-4 flex w-full flex-col items-stretch justify-end gap-2 md:flex-row md:items-center'>
				<div className='w-full md:w-auto'>
					<Tooltip title='Filtrar por estatus de la solicitud de factura' placement='top'>
						<div className='w-full md:w-auto'>
							<PopoverCheckboxFilter
								state={formData}
								setState={setFormData}
								property='status'
								options={optionsStatusRequestInvoiceFilter}
								title='Estatus de solicitud'
								subtitle='Filtra por estado de la solicitud de factura.'
								placeholderLabel='Estatus de solicitud'
								handleApllyFilters={applyFilters}
								handleRemoveFilter={removeFilters}
							/>
						</div>
					</Tooltip>
				</div>
				<div className='w-full md:w-auto'>
					<PopoverRangeDate
						state={formData}
						setState={setFormData}
						properties={['fromDate', 'toDate']}
						popoverTitle='Fecha de solicitud'
						popoverSubtitle='Filtra por rango o por fecha desde de la solicitud.'
						emptySummaryLabel='Fecha de solicitud'
						handleApllyFilters={() => applyFilters(formData)}
						handleRemoveFilter={removeFilters}
					/>
				</div>
				<div className='w-full md:w-auto'>
					<PopoverRadioFilter
						value={currentSortValue}
						onChange={handleSortChange}
						options={optionsSort}
						title='Ordenar solicitudes'
						subtitle='Define el orden de la lista por fecha de solicitud.'
						icon={ArrowsUpDownIcon}
						placeholderLabel='Ordenar por'
					/>
				</div>
			</div>

			<Wrapper>
				<SmartTable
					data={requestsInvoice}
					columns={columns}
					totalRows={totalRows}
					query={prefs.query}
					onQueryChange={patchQuery}
					onColumnSearchChange={handleColumnSearchChange}
					view={prefs.view}
					onViewChange={patchView}
					loading={loading}
					preferencesReady={prefsHydrated}
					emptyMessage='No hay solicitudes de facturas'
					pageSizeOptions={[10, 15, 25, 50]}
					onResetPrefs={resetPrefs}
				/>
			</Wrapper>

			<DrawerRequestInvoicePaymentDetails
				isOpen={isOpenDrawerPaymentDetails}
				toggleDrawer={toggleDrawerPaymentDetails}
				requestId={requestIdSelected}
			/>
		</ContainerFull>
	);
};
